import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { IconX } from "@tabler/icons-react";
import { useApiQuery, type Api } from "../../services/api";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { useNow } from "../../common/hooks/useNow";
import { createLocalStore } from "../../lib/store";
import type { SupportedLanguage } from "../../interfaces";
import { resolveCongressCountdownCopy } from "../../domains/congress/congressCountdownCopy";
import { resolveCongressNames } from "../../domains/congress/congressName";
import { isCongressHeroVisible } from "../../domains/congress/deriveCongressCountdown";
import { deriveCongressPhase } from "../../../../api/src/shared/domain/congress-lifecycle";
import {
  deriveCongressStatus,
  type CongressStatus,
} from "../../domains/congress/congressStatus";
import { formatCongressDay } from "../../domains/congress/congressDateLabel";
import { DEFAULT_COVER_COLOR } from "../../domains/congress/coverColor";
import { partnerAccentColor } from "../../domains/congress/partnerAccentColor";
import { resolveTopSessionsHeading } from "../../domains/congress/topSessionsHeading";
import { downloadCongressCalendarFile } from "../../domains/congress/congressCalendar";
import { getRoomName } from "../../domains/room/hooks/useRoomName";
import {
  gaEventCongressCountdownShown,
  gaEventCongressCountdownTapped,
  type CongressAnalyticsContext,
} from "../../tools/analytics/domains/congress.analytics";
import type { CongressCountdownCopy } from "../ui/CongressCard";
import { Button } from "../ui/Button";
import { DesktopCongressCountdown } from "./DesktopCongressCountdown";
import { DesktopCongressCover } from "./DesktopCongressCover";
import { DesktopCongressLiveBadge } from "./DesktopCongressLiveBadge";
import { isNativeApp } from "../../common/hooks/useIsDesktopView";

const MINUTE_MS = 60_000;
const PREVIEW_SESSIONS = 3;
const PREVIEW_ITEMS = 3;

type DiscoverySpace =
  Api["get"]["/webapp/private/discovery/congress-spaces"]["output"]["body"][number];
type JoinedRoom =
  Api["get"]["/webapp/private/rooms"]["output"]["body"]["rooms"][number];
type ContentBlock =
  Api["get"]["/webapp/private/congress-spaces/:id/content"]["output"]["body"][number];

type HeroPhase = "pre" | "live" | "post";

/**
 * What the reader did to the block, per space and per phase: ✕ folds it into a
 * one-line strip, ✕ on the strip hides it. Keyed on the phase, so a block put
 * away before the congress comes back when the congress opens.
 */
const useCongressHeroDismissals = createLocalStore(
  "desktop-congress-hero-dismissals",
  [],
  z.array(
    z.object({
      spaceId: z.string(),
      phase: z.enum(["pre", "live", "post"]),
      state: z.enum(["collapsed", "hidden"]),
    })
  )
);

function heroPhaseOf(status: CongressStatus): HeroPhase | null {
  switch (status.kind) {
    case "countdown":
      return "pre";
    case "live":
      return "live";
    case "ended":
      return "post";
    case "upcoming":
    case "evergreen":
      return null;
    default:
      throw new Error(status satisfies never);
  }
}

const PHASE_RANK = { live: 0, pre: 1, post: 2 } as const satisfies Record<
  HeroPhase,
  number
>;

/**
 * The congress block of the desktop For you feed (Figma "DesktopCongressHero"),
 * above the feed and never inside it. One congress at a time, the closest one:
 * a congress in progress first, then the next to open, then the one that ended
 * last. Same spaces and same window as the mobile Discovery hero: the spaces
 * promoted on Discovery, in a Room the reader follows, from the countdown to
 * the end of the post-congress window.
 */
export function DesktopCongressHero(): React.JSX.Element | null {
  const now = useNow(MINUTE_MS);
  const spacesQuery = useApiQuery(
    "/webapp/private/discovery/congress-spaces",
    {}
  );
  const roomsQuery = useApiQuery("/webapp/private/rooms", {});
  const [dismissals, setDismissals] = useCongressHeroDismissals();

  if (spacesQuery.state !== "success" || roomsQuery.state !== "success")
    return null;

  const closest = spacesQuery.body
    .flatMap((space) => {
      if (!isCongressHeroVisible(space, now)) return [];
      const status = deriveCongressStatus(space, now);
      const phase = heroPhaseOf(status);
      if (phase === null) return [];
      // A block the reader hid for this phase leaves the choice, so the next
      // closest congress can take its place.
      const hidden = dismissals.some(
        (entry) =>
          entry.spaceId === space.id &&
          entry.phase === phase &&
          entry.state === "hidden"
      );
      if (hidden) return [];
      // The endpoint only returns spaces of rooms the reader joined; the two
      // lists disagree between leaving a room and the next refetch.
      const room = roomsQuery.body.rooms.find(
        (candidate) => candidate.id === space.roomId
      );
      if (room === undefined) return [];
      // Closest first: the next to open, or the one that ended last.
      const anchor = phase === "post" ? space.endDate : space.startDate;
      if (anchor === null)
        throw new Error(
          "should not happen: a space in its countdown, live or post phase has both dates"
        );
      const order = phase === "post" ? -Date.parse(anchor) : Date.parse(anchor);
      return [{ space, status, phase, room, order }];
    })
    .sort((a, b) =>
      a.phase !== b.phase
        ? PHASE_RANK[a.phase] - PHASE_RANK[b.phase]
        : a.order - b.order
    )
    .at(0);
  if (closest === undefined) return null;

  const dismissal = dismissals.find(
    (entry) =>
      entry.spaceId === closest.space.id && entry.phase === closest.phase
  );

  const dismiss = (state: "collapsed" | "hidden") => {
    setDismissals((previous) => [
      ...previous.filter((entry) => entry.spaceId !== closest.space.id),
      { spaceId: closest.space.id, phase: closest.phase, state },
    ]);
  };

  return (
    <CongressHeroForSpace
      key={closest.space.id}
      space={closest.space}
      status={closest.status}
      phase={closest.phase}
      room={closest.room}
      collapsed={dismissal !== undefined}
      onCollapse={() => {
        dismiss("collapsed");
      }}
      onExpand={() => {
        setDismissals((previous) =>
          previous.filter((entry) => entry.spaceId !== closest.space.id)
        );
      }}
      onHide={() => {
        dismiss("hidden");
      }}
      now={now}
    />
  );
}

function CongressHeroForSpace({
  space,
  status,
  phase,
  room,
  collapsed,
  onCollapse,
  onExpand,
  onHide,
  now,
}: {
  space: DiscoverySpace;
  status: CongressStatus;
  phase: HeroPhase;
  room: JoinedRoom;
  collapsed: boolean;
  onCollapse: () => void;
  onExpand: () => void;
  onHide: () => void;
  now: Date;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  // Congress content (names, dates, sessions, countdown units) follows
  // `contentLang`; the block's own chrome stays on the interface locale.
  const { contentLang } = useActiveLang();
  const countdownCopy = resolveCongressCountdownCopy({ contentLang, t });
  const names = resolveCongressNames(
    space.translations,
    space.fallbackName,
    contentLang,
    space.city,
    space.country
  );
  const coverColor =
    space.coverColor === null ? DEFAULT_COVER_COLOR : space.coverColor;
  const accentColor = partnerAccentColor(room);
  const organisationName = room.organisationModel.name;
  const analytics = {
    spaceId: space.id,
    congressName: names.name,
    phase: deriveCongressPhase(space, now),
    roomId: space.roomId,
  } satisfies CongressAnalyticsContext;

  useEffectOnce(() => {
    gaEventCongressCountdownShown({
      context: analytics,
      surface: "discovery_banner",
    });
  }, [space.id, phase]);

  const spacePath = `/room/congress/${space.roomId}/${space.id}`;
  const openSpace = () => {
    gaEventCongressCountdownTapped({
      context: analytics,
      surface: "discovery_banner",
    });
    history.push({
      pathname: spacePath,
      state: { congressEntryPoint: "banner" },
    });
  };

  if (collapsed) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-ds-slate-100 bg-ds-white p-2.5 font-dm-sans">
        <button
          type="button"
          onClick={onExpand}
          aria-label={t("Show the congress")}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 border-none bg-transparent p-0 text-left"
        >
          <DesktopCongressCover
            cover={space.cover}
            coverColor={coverColor}
            name={names.name}
            className="h-8 w-15 rounded"
          />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="flex min-w-0 items-center gap-2 text-footnote text-ds-slate-600">
              {status.kind === "live" ? (
                <DesktopCongressLiveBadge label={countdownCopy.liveNow} />
              ) : null}
              <span className="truncate font-semibold text-ds-slate-500">
                {t(
                  "{{congressName}} · Shared by {{organisationName}} in {{roomName}}",
                  {
                    congressName: names.shortName,
                    organisationName,
                    roomName: getRoomName(room, contentLang),
                  }
                )}
              </span>
            </span>
            <span className="truncate text-footnote font-semibold text-ds-black">
              {names.name}
            </span>
          </span>
        </button>
        <Button variant="ghost" size="sm" onClick={openSpace}>
          {t("Open")}
        </Button>
        <button
          type="button"
          onClick={onHide}
          aria-label={t("Hide until the next phase")}
          className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-0 text-ds-slate-600 hover:bg-ds-slate-100"
        >
          <IconX className="size-4" stroke={1.5} />
        </button>
      </div>
    );
  }

  // Figma "DesktopCongressHero": the congress's tint before it, red during
  // it, neutral after it, as the space page's phase band.
  const headerBackground =
    phase === "live"
      ? "var(--color-ds-strawberry-50)"
      : phase === "post"
        ? "var(--color-ds-slate-50)"
        : `color-mix(in srgb, ${coverColor} 12%, white)`;
  const place = names.city === null || names.city === "" ? null : names.city;

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-ds-slate-100 bg-ds-white font-dm-sans">
      <div
        className="flex items-center gap-4 p-3"
        style={{ backgroundColor: headerBackground }}
      >
        <DesktopCongressCover
          cover={space.cover}
          coverColor={coverColor}
          name={names.name}
          className="h-20 w-44 rounded-xl"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <HeroStatus
            status={status}
            contentLang={contentLang}
            countdownCopy={countdownCopy}
            now={now}
          />
          <p className="m-0 text-heading-xs font-semibold text-ds-black">
            {names.name}
          </p>
          <p className="m-0 truncate text-footnote text-ds-slate-600">
            {place === null
              ? t("From {{roomName}} · shared by {{organisationName}}", {
                  roomName: getRoomName(room, contentLang),
                  organisationName,
                })
              : t(
                  "From {{roomName}} · shared by {{organisationName}} · {{city}}",
                  {
                    roomName: getRoomName(room, contentLang),
                    organisationName,
                    city: place,
                  }
                )}
          </p>
        </div>
        <button
          type="button"
          onClick={onCollapse}
          aria-label={t("Fold the congress block")}
          className="flex size-7 shrink-0 cursor-pointer items-center justify-center self-start rounded-md border-none bg-transparent p-0 text-ds-slate-600 hover:bg-ds-slate-100"
        >
          <IconX className="size-4" stroke={1.5} />
        </button>
      </div>
      <div className="flex items-stretch">
        <HeroSessions
          spaceId={space.id}
          shortName={names.shortName}
          phase={phase}
          contentLang={contentLang}
          now={now}
        />
        <HeroSpaceItems
          spaceId={space.id}
          accentColor={accentColor}
          contentLang={contentLang}
        />
      </div>
      <div className="flex items-center gap-4 border-t border-ds-slate-100 px-4 py-3">
        <Button variant="primary" size="sm" onClick={openSpace}>
          {phase === "post"
            ? t("Catch up on {{congressName}}", {
                congressName: names.shortName,
              })
            : t("Open congress space")}
        </Button>
        {/* The .ics is a blob: download, which the native shells cannot hand
            to a calendar app. */}
        {phase === "pre" &&
        !isNativeApp() &&
        space.startDate !== null &&
        space.endDate !== null ? (
          <button
            type="button"
            onClick={() => {
              if (space.startDate === null || space.endDate === null)
                throw new Error("should not happen: dates checked above");
              downloadCongressCalendarFile({
                uid: space.id,
                title: names.name,
                startDate: space.startDate,
                endDate: space.endDate,
                location: [names.city, names.country]
                  .filter((part) => part !== null && part !== "")
                  .join(", "),
                url: `${window.location.origin}${spacePath}`,
              });
            }}
            className="cursor-pointer border-none bg-transparent p-0 text-footnote font-semibold text-ds-mint-800"
          >
            {t("Add to calendar")}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function HeroStatus({
  status,
  contentLang,
  countdownCopy,
  now,
}: {
  status: CongressStatus;
  contentLang: SupportedLanguage;
  countdownCopy: CongressCountdownCopy;
  now: Date;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  switch (status.kind) {
    case "countdown":
      return (
        <div className="flex items-center gap-2">
          <DesktopCongressCountdown
            countdown={status.countdown}
            copy={countdownCopy}
          />
          <span className="text-desktop-meta font-semibold text-ds-slate-500">
            {t("Opens {{day}}", {
              day: formatCongressDay(status.opensOn, contentLang),
            })}
          </span>
        </div>
      );
    case "live":
      return (
        <div className="flex items-center gap-2">
          <DesktopCongressLiveBadge label={countdownCopy.liveNow} />
          <span className="text-desktop-meta font-semibold text-ds-slate-500">
            {t("{{date}} · Day {{day}} of {{total}}", {
              date: formatCongressDay(now, contentLang),
              day: status.day,
              total: status.dayCount,
            })}
          </span>
        </div>
      );
    case "ended":
      return (
        <span className="text-desktop-meta font-semibold text-ds-slate-500">
          {status.catchUpUntil === null
            ? t("Ended {{day}}", {
                day: formatCongressDay(status.endedOn, contentLang),
              })
            : t("Ended {{ended}} · Catch up until {{until}}", {
                ended: formatCongressDay(status.endedOn, contentLang),
                until: formatCongressDay(status.catchUpUntil, contentLang),
              })}
        </span>
      );
    case "upcoming":
    case "evergreen":
      return null;
    default:
      throw new Error(status satisfies never);
  }
}

/**
 * The curator's sessions, as a preview: the next ones before and during the
 * congress, the first ones after it. A space with no published sessions in the
 * reader's language answers 404, which is a state, not a failure: the column
 * is simply absent.
 */
function HeroSessions({
  spaceId,
  shortName,
  phase,
  contentLang,
  now,
}: {
  spaceId: string;
  shortName: string;
  phase: HeroPhase;
  contentLang: SupportedLanguage;
  now: Date;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const query = useApiQuery(
    "/webapp/private/congress-spaces/:id/top-sessions",
    { param: { id: spaceId }, query: { language: contentLang } }
  );
  if (query.state === "pending") return null;
  if (query.state === "error") {
    if (query.status === 404) return null;
    return (
      <p className="m-0 flex-1 p-4 text-footnote text-ds-slate-600">
        {query.message}
      </p>
    );
  }
  const sessions = query.body.sessions;
  // The curator ranks the sessions; the preview shows what comes next, so it
  // reads them in time order and keeps each one's rank.
  const upcoming =
    phase === "post"
      ? sessions
      : sessions
          .filter((session) => Date.parse(session.startsAt) >= now.getTime())
          .toSorted((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  const shown = upcoming.slice(0, PREVIEW_SESSIONS);
  const remaining = upcoming.length - shown.length;
  if (shown.length === 0) return null;
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2.5 p-4">
      <p className="m-0 text-desktop-meta font-semibold text-ds-slate-500">
        {resolveTopSessionsHeading({
          cardTitle: query.body.topSessionsCardTitle,
          shortName,
          sessionCount: sessions.length,
          contentLang,
          t,
        })}
      </p>
      {shown.map((session) => (
        <div key={session.id} className="flex items-start gap-2.5">
          <span className="flex size-5.5 shrink-0 items-center justify-center rounded-full bg-ds-mint-50 text-xs font-semibold text-ds-black">
            {sessions.indexOf(session) + 1}
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="text-xs text-ds-slate-600">
              {formatSessionMoment(session.startsAt, contentLang)}
            </span>
            <span className="line-clamp-2 text-footnote font-semibold text-ds-black">
              {session.title}
            </span>
          </span>
        </div>
      ))}
      {remaining > 0 ? (
        <p className="m-0 text-footnote text-ds-slate-600">
          {t("+ {{count}} more", { count: remaining })}
        </p>
      ) : null}
    </div>
  );
}

// "Sun 6 · 18:15", in UTC like every congress session time (see
// `CongressTopSessionsPage`).
function formatSessionMoment(
  startsAt: string,
  contentLang: SupportedLanguage
): string {
  const date = new Date(startsAt);
  const day = date.toLocaleDateString(contentLang, {
    weekday: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  const time = date.toLocaleTimeString(contentLang, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
  return `${day} · ${time}`;
}

function blockPreview(
  block: ContentBlock
): { label: string | null; title: string } | null {
  switch (block.kind) {
    case "thematicCollection":
      return {
        label: block.headerTitle,
        title: block.title,
      };
    case "pdfCard":
      return { label: block.labelType, title: block.documentTitle };
    case "article":
      return { label: null, title: block.article.title };
    case "video":
    case "infographic":
      return { label: null, title: block.content.title };
    case "podcast":
      return { label: null, title: block.podcast.title };
    case "topSessions":
      return null;
    default:
      throw new Error(block satisfies never);
  }
}

/**
 * What else the space holds, in the curator's order, in the partner's colour:
 * the whole space is partner content (option B of the brief).
 */
function HeroSpaceItems({
  spaceId,
  accentColor,
  contentLang,
}: {
  spaceId: string;
  accentColor: string;
  contentLang: SupportedLanguage;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const query = useApiQuery("/webapp/private/congress-spaces/:id/content", {
    param: { id: spaceId },
    query: { language: contentLang },
  });
  if (query.state !== "success") return null;
  const items = query.body
    .map((block) => {
      const preview = blockPreview(block);
      return preview === null ? null : { id: block.id, ...preview };
    })
    .filter((item) => item !== null);
  if (items.length === 0) return null;
  const shown = items.slice(0, PREVIEW_ITEMS);
  return (
    <div
      className="flex w-60 shrink-0 flex-col gap-3 p-4 text-footnote"
      style={{
        backgroundColor: `color-mix(in srgb, ${accentColor} 10%, white)`,
      }}
    >
      <p
        className="m-0 text-desktop-meta font-semibold"
        style={{ color: accentColor }}
      >
        {t("In this space")}
      </p>
      {shown.map((item) => (
        <div key={item.id} className="flex flex-col gap-0.5">
          {item.label === null ? null : (
            <span className="text-caption font-bold tracking-wider text-ds-slate-500 uppercase">
              {item.label}
            </span>
          )}
          <span className="line-clamp-2 font-semibold text-ds-black">
            {item.title}
          </span>
        </div>
      ))}
      {items.length > shown.length ? (
        <p className="m-0 text-ds-slate-600">
          {t("+ {{count}} more", { count: items.length - shown.length })}
        </p>
      ) : null}
    </div>
  );
}
